<template>
  <div id="app" :class="{ 'force-landscape': isPortrait }">
    <!-- 全螢幕按鈕 (僅在移動設備且非全螢幕時顯示) -->
    <button
      v-if="isMobile && !isFullscreen && !isIOS"
      @click="requestFullscreen"
      class="fullscreen-button"
    >
      <i class="fas fa-expand"></i>
    </button>

    <!-- iOS 提示 (僅在 iOS 設備上顯示) -->
    <div v-if="isIOS && !isIOSAdded" class="ios-instructions">
      <p>
        請點擊
        <i class="fas fa-share-square"></i>
        然後選擇「加到主畫面」以獲得全螢幕體驗
      </p>
      <button @click="dismissIOSInstructions">了解</button>
    </div>

    <!-- 應用內容：直向裝置進入需要橫向的頁面時，用 CSS 強制轉為橫向顯示，不再要求使用者實際旋轉裝置 -->
    <router-view />
  </div>
</template>

<script>
import signalRService from "@/assets/Js/signalR.js"; // 請確認路徑
import { fetchAuthStatus } from "@/assets/Js/auth.js";

export default {
  name: "App",
  data() {
    return {
      isFullscreen: false,
      isPortrait: false,
      isMobile: false,
      isIOS: false,
      isIOSAdded: false,
      orientationCheckInterval: null,
    };
  },
  mounted() {
    // 檢測設備類型
    this.isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    this.isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    // 檢測 iOS 是否已添加到主屏幕
    if (this.isIOS) {
      this.isIOSAdded = window.navigator.standalone === true;
    }

    // 檢測初始方向
    this.checkOrientation();

    // 設置事件監聽器
    window.addEventListener("resize", this.checkOrientation);

    // 監聽全螢幕變化
    document.addEventListener("fullscreenchange", this.fullscreenChanged);
    document.addEventListener("webkitfullscreenchange", this.fullscreenChanged);
    document.addEventListener("mozfullscreenchange", this.fullscreenChanged);
    document.addEventListener("MSFullscreenChange", this.fullscreenChanged);

    // 定期檢查方向
    this.orientationCheckInterval = setInterval(this.checkOrientation, 1000);

    // 自動嘗試全螢幕 (Android)
    if (this.isMobile && !this.isIOS) {
      // 等待用戶互動
      const tryFullscreen = () => {
        this.requestFullscreen();
        document.removeEventListener("touchstart", tryFullscreen);
        document.removeEventListener("click", tryFullscreen);
      };

      document.addEventListener("touchstart", tryFullscreen);
      document.addEventListener("click", tryFullscreen);
    }
    // [2. 新增：在 App 掛載時，初始化全域服務]
    this.initializeGlobalServices();
  },
  watch: {
    $route() {
      this.checkOrientation();
    },
  },
  methods: {
    checkOrientation() {
      const currentRoute = this.$route;
      const needLandscape = currentRoute.meta?.requireLandscape === true;
      const isPhysicallyPortrait = window.innerHeight > window.innerWidth;

      // 不再嘗試呼叫系統旋轉鎖定 API 或要求使用者旋轉裝置，
      // 只要頁面需要橫向、而裝置目前是直向，就直接用 CSS 強制轉為橫向顯示。
      this.isPortrait = needLandscape && isPhysicallyPortrait;
    },
    async requestFullscreen() {
      const docEl = document.documentElement;

      try {
        let requestResult = null;

        if (docEl.requestFullscreen) {
          requestResult = docEl.requestFullscreen();
        } else if (docEl.webkitRequestFullscreen) {
          requestResult = docEl.webkitRequestFullscreen();
        } else if (docEl.mozRequestFullScreen) {
          requestResult = docEl.mozRequestFullScreen();
        } else if (docEl.msRequestFullscreen) {
          requestResult = docEl.msRequestFullscreen();
        } else {
          return false;
        }

        // Fullscreen API 會回傳 Promise；必須 await 才能攔截瀏覽器的權限拒絕。
        if (requestResult && typeof requestResult.then === "function") {
          await requestResult;
        }

        return true;
      } catch (error) {
        // iframe、權限政策或瀏覽器設定可能拒絕全螢幕。
        // 這是預期的瀏覽器限制，不應形成 Vue 未捕捉錯誤遮罩。
        console.warn("瀏覽器拒絕全螢幕請求：", error);
        return false;
      }
    },
    fullscreenChanged() {
      this.isFullscreen = Boolean(
        document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
      );
    },
    dismissIOSInstructions() {
      // 保存用戶已經看過iOS說明
      localStorage.setItem("iosInstructionsDismissed", "true");
      this.isIOSAdded = true;
    },

    async initializeGlobalServices() {
      try {
        // 透過後端 /auth/status 確認 Cookie 登入狀態，不讀取 HttpOnly token。
        const authStatus = await fetchAuthStatus({ force: false });
        const storeId = authStatus?.storeId;

        if (authStatus?.authenticated && storeId) {
          console.log(`準備連接 SignalR 並加入群組 store_${storeId}`);
          await signalRService.start(storeId); // 🎯 傳入 storeId
          console.log(`✅ SignalR 服務已啟動`);
        }
      } catch (err) {
        console.error("❌ 啟動全域 SignalR 服務失敗:", err);
      }
    },
  },
  created() {
    // 檢查iOS說明是否已被關閉
    if (localStorage.getItem("iosInstructionsDismissed") === "true") {
      this.isIOSAdded = true;
    }
  },
  beforeUnmount() {
    // 清理
    window.removeEventListener("resize", this.checkOrientation);
    document.removeEventListener("fullscreenchange", this.fullscreenChanged);
    document.removeEventListener(
      "webkitfullscreenchange",
      this.fullscreenChanged
    );
    document.removeEventListener("mozfullscreenchange", this.fullscreenChanged);
    document.removeEventListener("MSFullscreenChange", this.fullscreenChanged);

    clearInterval(this.orientationCheckInterval);
  },
};
</script>

<style>
/* ====================================
  主體區域 - 控制整個頁面的基本設置
   ==================================== */
html,
body,
#app {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* ====================================
  強制橫向顯示 - 裝置為直向、但目前頁面需要橫向時，
  不再彈出「請旋轉設備」的提示，而是直接用 CSS 把整個
  #app 旋轉 90 度並置換寬高，讓使用者不用真的轉動裝置
  也能看到橫向畫面。
   ==================================== */
#app.force-landscape {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vh;
  height: 100vw;
  transform-origin: top left;
  transform: rotate(90deg) translateY(-100%);
  overflow: hidden;
}

/* ====================================
  iOS 安裝提示區域 - 提示加到主畫面
   ==================================== */
.ios-instructions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 15px;
  text-align: center;
  z-index: 9998;
}

.ios-instructions button {
  background: #2c3e50;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  margin-top: 10px;
}

/* ====================================
  全螢幕按鈕區域 - 提供使用者切換全螢幕
   ==================================== */
.fullscreen-button {
  position: fixed;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9997;
  cursor: pointer;
}
</style>
